import * as React from 'react';

import { faArrowRightFromBracket, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import { useExitInParkingCard } from 'src/features/cards/api/useExitInParkingCard';
import ExitInParkingCardForm from 'src/features/cards/forms/ExitInParkingCardForm';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { InParkingCardDetail } from 'src/features/cards/types/card';

const exitInParkingCardFormSchema = z.object({
    open_point: z.boolean(),
    exit_point_id: z.string().min(1),
});
type ExitInParkingCardFormValues = z.infer<typeof exitInParkingCardFormSchema>;

interface ExitInParkingCardModalFormProps extends React.HTMLAttributes<HTMLDivElement> {
    inParkingCardDetail: InParkingCardDetail;
    refetch: () => void;
}

const ExitInParkingCardModalForm = ({ children, inParkingCardDetail, refetch }: ExitInParkingCardModalFormProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: payInParkingCard, isPending } = useExitInParkingCard({
        onSuccess: (_ciw, data) => {
            refetch();
            onOpenChange(false);
            toast({
                title: formatMessage(cardMessages.cardExitSuccess),
                description: formatMessage(cardMessages.cardExitSucccesDetails, {
                    client: inParkingCardDetail.card_number,
                    point: data.exit_point_id,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const form = useForm<ExitInParkingCardFormValues>({
        resolver: zodResolver(exitInParkingCardFormSchema),
        defaultValues: {
            open_point: false,
            exit_point_id: '',
        },
    });

    const onSubmit = (values: ExitInParkingCardFormValues) => {
        const submitData = {
            parkingId,
            cardId: inParkingCardDetail.in_parking_card_id,
            ...values,
        };

        payInParkingCard(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faArrowRightFromBracket} />
                        <div>
                            <DialogTitle>{formatMessage(cardMessages.exitFromParking)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(cardMessages.exitClientFromParking, {
                                    client: inParkingCardDetail.card_number,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <ExitInParkingCardForm form={form} onSubmit={onSubmit} />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button
                        disabled={isPending || !form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(generalMessages.exit)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { ExitInParkingCardModalForm, exitInParkingCardFormSchema };
export type { ExitInParkingCardFormValues };
